import type { Banner, Promocode, Labels, Stories, PageInfo } from '@@partner:types:app'
import type { NestedFilter, CommonPager, CommonSort } from '@@partner:types:graphql'

import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

export interface MarketingDashboardQuery {
  // Баннеры истекающие в ближайшие 7 дней
  expiringBanners: {
    edges: Banner[]
    pageInfo: PageInfo
  }
  // Будущие баннеры (activeFrom > now)
  upcomingBanners: {
    edges: Banner[]
    pageInfo: PageInfo
  }
  // Активные баннеры
  activeBanners: {
    pageInfo: PageInfo
  }
  // Истории истекающие
  expiringStories: {
    edges: Stories[]
    pageInfo: PageInfo
  }
  // Будущие истории
  upcomingStories: {
    edges: Stories[]
    pageInfo: PageInfo
  }
  // Активные истории
  activeStories: {
    pageInfo: PageInfo
  }
  // Лейблы истекающие
  expiringLabels: {
    edges: Labels[]
    pageInfo: PageInfo
  }
  // Будущие лейблы
  upcomingLabels: {
    edges: Labels[]
    pageInfo: PageInfo
  }
  // Активные лейблы
  activeLabels: {
    pageInfo: PageInfo
  }
  // Промокоды истекающие
  expiringPromocodes: {
    edges: Promocode[]
    pageInfo: PageInfo
  }
  // Будущие промокоды
  upcomingPromocodes: {
    edges: Promocode[]
    pageInfo: PageInfo
  }
  // Активные промокоды
  activePromocodes: {
    pageInfo: PageInfo
  }
  // Топ промокодов по активациям
  topPromocodes: {
    edges: Promocode[]
    pageInfo: PageInfo
  }
}

export interface MarketingDashboardVariables {
  expiringBannersFilter: NestedFilter
  upcomingBannersFilter: NestedFilter
  activeBannersFilter: NestedFilter
  expiringStoriesFilter: NestedFilter
  upcomingStoriesFilter: NestedFilter
  activeStoriesFilter: NestedFilter
  expiringLabelsFilter: NestedFilter
  upcomingLabelsFilter: NestedFilter
  activeLabelsFilter: NestedFilter
  expiringPromocodesFilter: NestedFilter
  upcomingPromocodesFilter: NestedFilter
  activePromocodesFilter: NestedFilter
  topPromocodesFilter: NestedFilter
  expiringPager: CommonPager
  upcomingPager: CommonPager
  countPager: CommonPager
  topPager: CommonPager
  expiringSort: CommonSort
  upcomingSort: CommonSort
  expiringPromocodesSort: CommonSort
  upcomingPromocodesSort: CommonSort
  topPromocodesSort: CommonSort
}

export const GQL_QUERY_MARKETING_DASHBOARD = gql`
  query MarketingDashboard(
    $expiringBannersFilter: NestedFilterInput
    $upcomingBannersFilter: NestedFilterInput
    $activeBannersFilter: NestedFilterInput
    $expiringStoriesFilter: NestedFilterInput
    $upcomingStoriesFilter: NestedFilterInput
    $activeStoriesFilter: NestedFilterInput
    $expiringLabelsFilter: NestedFilterInput
    $upcomingLabelsFilter: NestedFilterInput
    $activeLabelsFilter: NestedFilterInput
    $expiringPromocodesFilter: NestedFilterInput
    $upcomingPromocodesFilter: NestedFilterInput
    $activePromocodesFilter: NestedFilterInput
    $topPromocodesFilter: NestedFilterInput
    $expiringPager: CommonPagerInput
    $upcomingPager: CommonPagerInput
    $countPager: CommonPagerInput
    $topPager: CommonPagerInput
    $expiringSort: CommonSortInput
    $upcomingSort: CommonSortInput
    $expiringPromocodesSort: CommonSortInput
    $upcomingPromocodesSort: CommonSortInput
    $topPromocodesSort: CommonSortInput
  ) {
    # Баннеры с истекающим сроком
    expiringBanners: allBanners(
      filter: $expiringBannersFilter
      pager: $expiringPager
      sort: $expiringSort
    ) {
      edges {
        id
        name
        activeUntil
        activeFrom
        status
        bannerSlide(style: "400xauto") {
          url
        }
      }
      pageInfo {
        total
      }
    }

    # Будущие баннеры
    upcomingBanners: allBanners(
      filter: $upcomingBannersFilter
      pager: $upcomingPager
      sort: $upcomingSort
    ) {
      edges {
        id
        name
        activeFrom
        activeUntil
        status
        bannerSlide(style: "400xauto") {
          url
        }
      }
      pageInfo {
        total
      }
    }
    
    # Всего активных баннеров
    activeBanners: allBanners(
      filter: $activeBannersFilter
      pager: $countPager
    ) {
      pageInfo {
        total
      }
    }
    
    # Истории с истекающим сроком
    expiringStories: allMarketingStories(
      filter: $expiringStoriesFilter
      pager: $expiringPager
      sort: $expiringSort
    ) {
      edges {
        id
        name
        activeFrom
        activeUntil
        status
        preview {
          url
        }
      }
      pageInfo {
        total
      }
    }
    
    # Будущие истории
    upcomingStories: allMarketingStories(
      filter: $upcomingStoriesFilter
      pager: $upcomingPager
      sort: $upcomingSort
    ) {
      edges {
        id
        name
        activeFrom
        activeUntil
        status
        preview {
          url
        }
      }
      pageInfo {
        total
      }
    }
    
    # Всего активных историй
    activeStories: allMarketingStories(
      filter: $activeStoriesFilter
      pager: $countPager
    ) {
      pageInfo {
        total
      }
    }
    
    # Лейблы с истекающим сроком
    expiringLabels: allMarketingLabels(
      filter: $expiringLabelsFilter
      pager: $expiringPager
      sort: $expiringSort
    ) {
      edges {
        id
        name
        activeFrom
        activeUntil
        status
        image {
          url
        }
      }
      pageInfo {
        total
      }
    }
    
    # Будущие лейблы
    upcomingLabels: allMarketingLabels(
      filter: $upcomingLabelsFilter
      pager: $upcomingPager
      sort: $upcomingSort
    ) {
      edges {
        id
        name
        activeFrom
        activeUntil
        status
        image {
          url
        }
      }
      pageInfo {
        total
      }
    }
    
    # Всего активных лейблов
    activeLabels: allMarketingLabels(
      filter: $activeLabelsFilter
      pager: $countPager
    ) {
      pageInfo {
        total
      }
    }
    
    # Промокоды с истекающим сроком
    expiringPromocodes: allPromocodes(
      filter: $expiringPromocodesFilter
      pager: $expiringPager
      sort: $expiringPromocodesSort
    ) {
      edges {
        id
        code
        active_from
        active_until
        is_active
        activated_count
      }
      pageInfo {
        total
      }
    }
    
    # Будущие промокоды
    upcomingPromocodes: allPromocodes(
      filter: $upcomingPromocodesFilter
      pager: $upcomingPager
      sort: $upcomingPromocodesSort
    ) {
      edges {
        id
        code
        active_from
        active_until
        is_active
        activated_count
      }
      pageInfo {
        total
      }
    }
    
    # Всего активных промокодов
    activePromocodes: allPromocodes(
      filter: $activePromocodesFilter
      pager: $countPager
    ) {
      pageInfo {
        total
      }
    }
    
    # Топ промокодов по активациям
    topPromocodes: allPromocodes(
      filter: $topPromocodesFilter
      pager: $topPager
      sort: $topPromocodesSort
    ) {
      edges {
        id
        code
        activated_count
        max_total_activation_count
        active_until
      }
      pageInfo {
        total
      }
    }
  }
`

export const useFetchMarketingDashboard = () => {
  const variables = useMemo<MarketingDashboardVariables>(() => {
    const now = Date.now()
    const expiringDate = now + 7 * 24 * 60 * 60 * 1000 // +7 дней

    // Фильтр активных элементов
    const activeFilter = { fields: [ 'status' ], op: 'eq', value: [ '1' ] }
    
    // Фильтр истекающих баннеров/сторис/лейблов
    const expiringActiveFilter = {
      and: [
        activeFilter,
        { fields: [ 'activeUntil' ], op: 'gte', value: [ now ] },
        { fields: [ 'activeUntil' ], op: 'lte', value: [ expiringDate ] }
      ]
    }

    // Фильтр будущих элементов (activeFrom > now)
    const upcomingFilter = {
      and: [
        activeFilter,
        { fields: [ 'activeFrom' ], op: 'gt', value: [ now ] }
      ]
    }

    // Фильтр активных промокодов
    const activePromocodesBase = {
      and: [
        { fields: [ 'type' ], op: 'eq', value: [ 'promocode' ] },
        { fields: [ 'is_active' ], op: 'eq', value: [ 'true' ] }
      ]
    }

    // Фильтр истекающих промокодов (active_until это Date, передаем ISO строку)
    const nowDate = new Date(now).toISOString()
    const expiringDateIso = new Date(expiringDate).toISOString()
    
    const expiringPromocodesFilter = {
      and: [
        { fields: [ 'type' ], op: 'eq', value: [ 'promocode' ] },
        { fields: [ 'is_active' ], op: 'eq', value: [ 'true' ] },
        { fields: [ 'active_until' ], op: 'gte', value: [ nowDate ] },
        { fields: [ 'active_until' ], op: 'lte', value: [ expiringDateIso ] }
      ]
    }

    // Фильтр будущих промокодов (active_from > now)
    const upcomingPromocodesFilter = {
      and: [
        { fields: [ 'type' ], op: 'eq', value: [ 'promocode' ] },
        { fields: [ 'is_active' ], op: 'eq', value: [ 'true' ] },
        { fields: [ 'active_from' ], op: 'gt', value: [ nowDate ] }
      ]
    }

    return {
      expiringBannersFilter: expiringActiveFilter,
      upcomingBannersFilter: upcomingFilter,
      activeBannersFilter: activeFilter,
      expiringStoriesFilter: expiringActiveFilter,
      upcomingStoriesFilter: upcomingFilter,
      activeStoriesFilter: activeFilter,
      expiringLabelsFilter: expiringActiveFilter,
      upcomingLabelsFilter: upcomingFilter,
      activeLabelsFilter: activeFilter,
      expiringPromocodesFilter: expiringPromocodesFilter,
      upcomingPromocodesFilter: upcomingPromocodesFilter,
      activePromocodesFilter: activePromocodesBase,
      topPromocodesFilter: activePromocodesBase,
      expiringPager: { page: 1, limit: 10 },
      upcomingPager: { page: 1, limit: 10 },
      countPager: { page: 1, limit: 1 },
      topPager: { page: 1, limit: 5 },
      expiringSort: { fieldName: 'ts_active_from', direction: 1 },
      upcomingSort: { fieldName: 'ts_active_from', direction: 1 },
      expiringPromocodesSort: { fieldName: 'active_until', direction: 1 },
      upcomingPromocodesSort: { fieldName: 'active_from', direction: 1 },
      topPromocodesSort: { fieldName: 'active_until', direction: 1 }
    }
  }, [])

  return useQuery<MarketingDashboardQuery, MarketingDashboardVariables>(
    GQL_QUERY_MARKETING_DASHBOARD,
    {
      variables,
      fetchPolicy: 'cache-and-network'
    }
  )
}
